import type { Cobranca } from '@/api/endpoints/cobrancas';

// Cobranças de exemplo do modo mock (AbacatePay). Dados fictícios.

const horasAtras = (h: number) => new Date(Date.now() - h * 3600_000).toISOString();

// QR de mentira (padrão fixo de quadrados) só para a tela ter a imagem.
export function qrDeMentira(semente: string): string {
  const n = 25;
  let h = 0;
  for (const ch of semente) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  const rects: string[] = [];
  const finder = (x: number, y: number) =>
    `<rect x="${x}" y="${y}" width="7" height="7" fill="#000"/><rect x="${x + 1}" y="${y + 1}" width="5" height="5" fill="#fff"/><rect x="${x + 2}" y="${y + 2}" width="3" height="3" fill="#000"/>`;
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      const noFinder = (x < 8 && y < 8) || (x > n - 9 && y < 8) || (x < 8 && y > n - 9);
      if (noFinder) continue;
      h = (h * 1103515245 + 12345) >>> 0;
      if (h % 100 < 48) rects.push(`<rect x="${x}" y="${y}" width="1" height="1" fill="#000"/>`);
    }
  }
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="-1 -1 ${n + 2} ${n + 2}" shape-rendering="crispEdges"><rect x="-1" y="-1" width="${n + 2}" height="${n + 2}" fill="#fff"/>${finder(0, 0)}${finder(n - 7, 0)}${finder(0, n - 7)}${rects.join('')}</svg>`;
  return `data:image/svg+xml;base64,${btoa(svg)}`;
}

export const brCodeDeMentira = (id: string) =>
  `00020101021226950014br.gov.bcb.pix2573pix.abacatepay.com/qr/v2/${id}5204000053039865802BR5925CURRAL TECH LTDA6009SAO PAULO62070503***6304A1B2`;

const base = {
  provedor_id: null,
  descricao: null,
  plano_id: null,
  plano_nome: null,
  plano_codigo: null,
  ciclo: null,
  produtor_user_id: null,
  produtor_nome: null,
  cliente_nome: null,
  cliente_email: null,
  cliente_telefone: null,
  cliente_documento: null,
  pix_copia_cola: null,
  pix_qrcode_base64: null,
  boleto_linha_digitavel: null,
  boleto_codigo_barras: null,
  boleto_url: null,
  boleto_vencimento: null,
  cartao_bandeira: null,
  cartao_final: null,
  parcelas: null,
  parcelas_max: null,
  taxa_centavos: null,
  comprovante_url: null,
  expira_em: null,
  paga_em: null,
  reembolsada_em: null,
  modo_teste: false,
} satisfies Partial<Cobranca>;

const exemplos: Omit<Cobranca, 'token_publico' | 'link_pagamento'>[] = [
  {
    ...base,
    id: 6,
    provedor_id: 'pix_char_mock6',
    metodo: 'PIX',
    status: 'PENDENTE',
    valor_centavos: 99900,
    plano_id: 2,
    plano_nome: 'Profissional',
    plano_codigo: 'profissional',
    ciclo: 'anual',
    produtor_user_id: 1,
    produtor_nome: 'João Batista Ferreira',
    cliente_nome: 'João Batista Ferreira',
    cliente_telefone: '+5567989352424',
    cliente_documento: '***.456.789-**',
    pix_copia_cola: brCodeDeMentira('mock6'),
    pix_qrcode_base64: qrDeMentira('mock6'),
    expira_em: new Date(Date.now() + 3 * 86400_000).toISOString(),
    created_at: horasAtras(1),
  },
  {
    ...base,
    id: 5,
    provedor_id: 'card_mock5',
    metodo: 'CARTAO',
    status: 'PAGA',
    valor_centavos: 9990,
    plano_id: 2,
    plano_nome: 'Profissional',
    plano_codigo: 'profissional',
    ciclo: 'mensal',
    produtor_user_id: 2,
    produtor_nome: 'Maria Aparecida Souza',
    cliente_nome: 'Maria Aparecida Souza',
    cliente_telefone: '+5565999112233',
    cartao_bandeira: 'VISA',
    cartao_final: '4242',
    parcelas: 1,
    parcelas_max: 1,
    taxa_centavos: 489,
    comprovante_url: 'https://app.abacatepay.com/receipt/mock5',
    paga_em: horasAtras(20),
    created_at: horasAtras(22),
  },
  {
    ...base,
    id: 4,
    provedor_id: 'pix_char_mock4',
    metodo: 'PIX',
    status: 'PAGA',
    valor_centavos: 3990,
    plano_id: 1,
    plano_nome: 'Básico',
    plano_codigo: 'basico',
    ciclo: 'mensal',
    produtor_user_id: 3,
    produtor_nome: 'Elan Teixeira dos Reis',
    cliente_nome: 'Elan Teixeira dos Reis',
    cliente_telefone: '+5579999815441',
    taxa_centavos: 80,
    comprovante_url: 'https://app.abacatepay.com/receipt/mock4',
    paga_em: horasAtras(46),
    created_at: horasAtras(47),
  },
  {
    ...base,
    id: 3,
    provedor_id: 'bol_mock3',
    metodo: 'BOLETO',
    status: 'PENDENTE',
    valor_centavos: 45000,
    descricao: 'Instalação do bastão RFID',
    cliente_nome: 'Agropecuária Santa Fé',
    cliente_documento: '**.345.678/0001-**',
    cliente_telefone: '+5594988776655',
    boleto_linha_digitavel: '23793.38128 60082.677713 52000.063304 1 99990000045000',
    boleto_url: 'https://app.abacatepay.com/boleto/mock3.pdf',
    pix_copia_cola: brCodeDeMentira('mock3'),
    expira_em: new Date(Date.now() + 2 * 86400_000).toISOString(),
    created_at: horasAtras(70),
  },
  {
    ...base,
    id: 2,
    provedor_id: 'pix_char_mock2',
    metodo: 'PIX',
    status: 'EXPIRADA',
    valor_centavos: 15000,
    descricao: 'Treinamento da equipe',
    cliente_nome: 'Fazenda Boa Vista',
    created_at: horasAtras(24 * 6),
  },
  {
    ...base,
    id: 1,
    provedor_id: 'pix_char_mock1',
    metodo: 'PIX',
    status: 'REEMBOLSADA',
    valor_centavos: 3990,
    plano_id: 1,
    plano_nome: 'Básico',
    plano_codigo: 'basico',
    ciclo: 'mensal',
    produtor_user_id: 4,
    produtor_nome: 'Carlos Henrique Lima',
    taxa_centavos: 80,
    paga_em: horasAtras(24 * 9),
    reembolsada_em: horasAtras(24 * 8),
    created_at: horasAtras(24 * 9 + 1),
  },
];

export const tokenDeMentira = (id: number) =>
  `mOcK${String(id).padStart(4, '0')}TokenPublicoDeTeste01`;

export const cobrancasExemplo: Cobranca[] = exemplos.map((c) => ({
  ...c,
  token_publico: tokenDeMentira(c.id),
  link_pagamento: `https://painel.curraltech.com/pagar/${tokenDeMentira(c.id)}`,
}));
